import { createContext, useContext, useMemo } from 'react';
import { z } from 'zod';
import { permissionSchema } from '#app/utils/user.ts';

type CheckPermissions = (permission: string) => boolean;

// Define context type
type PermissionsContextType = {
  permissions: string[];
  checkPermissions: CheckPermissions;
};

// Create context
const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

// Permissions array schema
const permissionsSchema = z.array(permissionSchema);

const isDev = process.env.NODE_ENV !== 'production';

// Factory function to create a checkPermissions function
const makeCheckPermissions = (permissions: string[]) => {
  return (permission: string) => {
    return permissions.includes(permission);
  };
};

type PermissionsProviderProps = {
  children: React.ReactNode;
  allowedPermissions: string[];
};

export const PermissionsProvider = ({ children, allowedPermissions }: PermissionsProviderProps) => {
  // Get permissions from parent PermissionProvider if any
  const parentPermissionContext = useContext(PermissionsContext);

  // Merge permissions from parent and allowed permissions
  const mergedPermissions = useMemo(() => {
    const inheritedPermissions = parentPermissionContext?.permissions || [];

    return Array.from(new Set([...inheritedPermissions, ...allowedPermissions]));
  }, [parentPermissionContext?.permissions, allowedPermissions]);

  if (!permissionsSchema.safeParse(mergedPermissions).success && isDev) {
    throw new Error('Error: Invalid permissions provided to PermissionsProvider');
  }

  const contextValue = useMemo(
    () => ({
      permissions: mergedPermissions,
      checkPermissions: makeCheckPermissions(mergedPermissions),
    }),
    [mergedPermissions]
  );

  return <PermissionsContext.Provider value={contextValue}>{children}</PermissionsContext.Provider>;
};

export type PermissionOptions = {
  /**
   * Action (i.e. create, read, update, delete)
   */
  I: string;

  /**
   * Entity (i.e. user, order_documents, discounts, coupons, etc.)
   * Can be provided as `a`, `an`, or `this`
   */
  a?: string;
  an?: string;
  this?: string;

  /**
   * Scope of the permission (own resources or any resources)
   */
  scope?: 'own' | 'any';
};

export const useCheckPermissions = () => {
  const context = useContext(PermissionsContext);
  const defaultCheckPermissions = context?.checkPermissions ?? makeCheckPermissions(context?.permissions || []);

  const can = ({ I, a, an, this: thisProp, scope }: PermissionOptions, allowedPermissions?: string[]) => {
    // Error if not within a PermissionsProvider or no allowedPermissions are provided
    if (isDev && !allowedPermissions && (!context || !context.permissions.length)) {
      throw new Error('Error: useCheckPermissions must be used within a PermissionsProvider');
    }

    // Use whichever entity prop is provided (a, an, or this)
    const entity = a || an || thisProp;

    if (!I || !entity) {
      if (isDev) {
        console.warn(
          'Can component is used without an action ("I") and entity ("a", "an", or "this") props. This will always return false.'
        );
      }
      return false;
    }

    const permission = [I, entity, scope].filter(Boolean).join(':');

    // If allowedPermissions are provided, use them to check permissions, otherwise use the default checkPermissions function
    const checkPermissions = allowedPermissions ? makeCheckPermissions(allowedPermissions) : defaultCheckPermissions;

    return checkPermissions(permission);
  };

  return { can };
};
